import {
	refreshTokenRequest,
	loginRequest,
	loginSuccess,
	logoutRequest,
	authInitState,
	getTokenSuccess,
	getTokenRequest,
	getConnectRequest,
	getConnectSuccess,
	getPasswordPolicyRequest,
	getPasswordPolicySuccess,
	refreshTokenSuccess,
	refreshTokenByTimeOutRequest,
	refreshTokenByTimeOutSuccess,
	setRefreshTokenByTimeOutLoading,
} from 'redux/reducers/auth/reducer';
import axios from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { notificationContainer } from 'utils/notificationContainer';
import { api, responseErrors } from 'services';
import { DateTime } from 'luxon';

import {
	IAdminAuthUserResponseData,
	ILoginRequestPayload,
	ITokenRequestPayload,
	ITokenResponsePayload,
	IAuthConnectRequestPayload,
	IAuthConnectResponsePayload,
	ILogoutRequestPayload,
	IAuthRefreshTokenRequestPayload,
} from './types';
import { getAuthConnect, getRefreshTime, getRefreshTokenLoader } from './selectors';
import { getUserInfo } from '../user/selectors';
import { getUserInfoRequest } from '../user/reducer';

// =============================================================:
function* loginRequestWorker(action: PayloadAction<ILoginRequestPayload>) {
	const { payload } = action;

	try {
		const response: IAdminAuthUserResponseData = yield call(api.auth.login, payload);

		yield put(loginSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error?.response?.data?.errors?.totp) {
				const code: string = error?.response?.data?.errors?.totp[0];
				notificationContainer(code, 'info');
			} else {
				notificationContainer('The login or password is incorrect ', 'error');
			}
		}
	}
}

// =============================================================:
function* getTokenWorker(action: PayloadAction<ITokenRequestPayload>) {
	const { payload } = action;

	try {
		const response: ITokenResponsePayload = yield call(api.auth.tokenCode, payload);

		yield put(getTokenSuccess(response));
		yield put(getUserInfoRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error?.response?.data?.errors?.totp) {
				const code: string = error?.response?.data?.errors?.totp[0];
				notificationContainer(code, 'info');
			} else {
				notificationContainer('The login or password is incorrect ', 'error');
			}
		}
	}
}

// =============================================================:
function* logoutRequestWorker(action: PayloadAction<ILogoutRequestPayload>) {
	const { payload } = action;

	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.auth.logout, payload);
		yield put(authInitState());
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
		window.location.href = '/';
	}
}

// =============================================================:

function* refreshTokenWorker(action: PayloadAction<IAuthRefreshTokenRequestPayload>) {
	const { payload } = action;
	try {
		// console.log(DateTime.now().toFormat('HH:mm:ss'), 'refreshTokenWorker payload', payload);

		const response: IAuthConnectResponsePayload = yield call(api.auth.refreshToken, payload);
		if (response?.access_token && response?.refresh_token) yield put(getConnectSuccess(response));
		yield put(refreshTokenSuccess(Date.now() + 1000 * (response?.expires_at ?? 0) - 10_000));

		// yield put(getTokenSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
		window.location.href = '/';
	}
}

// =============================================================:

export function* refreshTokenByTimeOutWorker() {
	try {
		console.log('----------------------------');
		const refreshTime: number = yield select(getRefreshTime);
		console.log(
			DateTime.now().toFormat('HH:mm:ss'),
			'refreshTokenByTimeOutWorker START refreshTime',
			DateTime.fromMillis(refreshTime).toFormat('yyyy.MM.dd HH:mm:ss'),
		);
		const refreshTokenIsLoading: boolean = yield select(getRefreshTokenLoader);
		console.log(
			DateTime.now().toFormat('HH:mm:ss'),
			'refreshTokenByTimeOutWorker Is Loading',
			refreshTokenIsLoading,
		);

		if (!refreshTime) {
			yield put(refreshTokenByTimeOutSuccess());
			return;
		}
		if (refreshTime > Date.now()) {
			yield put(refreshTokenByTimeOutSuccess());
			console.log(
				DateTime.now().toFormat('HH:mm:ss'),
				'refreshTokenByTimeOutWorker refreshTime > Date.now',
			);
			return;
		}
		if (refreshTokenIsLoading) return;

		const payload: IAuthConnectResponsePayload = yield select(getAuthConnect);
		console.log(DateTime.now().toFormat('HH:mm:ss'), 'REFRESH_TOKEN', !!payload?.refresh_token);
		if (!payload?.refresh_token) return;
		console.log(
			DateTime.now().toFormat('HH:mm:ss'),
			'refreshTokenByTimeOutWorker payload',
			payload,
		);

		yield put(setRefreshTokenByTimeOutLoading());
		const response: IAuthConnectResponsePayload = yield call(api.auth.refreshToken, payload);

		if (response?.access_token && response?.refresh_token) {
			yield put(getConnectSuccess(response));

			const refreshTimeNew: number = Date.now() + 1000 * (response?.expires_at ?? 0) - 10_000;

			yield put(refreshTokenSuccess(refreshTimeNew));
			console.log(
				DateTime.now().toFormat('HH:mm:ss'),
				'refreshTokenByTimeOutWorker Success refreshTimeNew',
				DateTime.fromMillis(refreshTimeNew).toFormat('HH:mm:ss'),
			);
			yield put(refreshTokenByTimeOutSuccess());
		}
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
		window.location.href = '/';
	}
}

// =============================================================:

function* getConnectWorker(action: PayloadAction<IAuthConnectRequestPayload>) {
	const { payload } = action;

	try {
		// yield saveUrl(window.location.pathname + window.location.search);
		// console.log(DateTime.now().toFormat('HH:mm:ss'), 'getConnectWorker payload', payload);

		const response: IAuthConnectResponsePayload = yield call(api.auth.connect, payload);

		yield put(loginSuccess({ token: response?.access_token ?? null }));
		yield put(getConnectSuccess(response));
		yield put(refreshTokenSuccess(Date.now() + 1000 * (response?.expires_at ?? 0) - 10_000));
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
		window.location.href = '/';
	}
}

function* getPasswordPolicyWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: string = yield call(api.auth.passwordPolicy);

		yield put(getPasswordPolicySuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
	}
}

// =============================================================:
export function* authSaga() {
	yield takeEvery(loginRequest.type, loginRequestWorker);
	yield takeEvery(refreshTokenRequest.type, refreshTokenWorker);
	yield takeEvery(logoutRequest.type, logoutRequestWorker);
	yield takeEvery(getTokenRequest.type, getTokenWorker);
	yield takeEvery(getConnectRequest.type, getConnectWorker);
	yield takeEvery(getPasswordPolicyRequest.type, getPasswordPolicyWorker);
	yield takeEvery(refreshTokenByTimeOutRequest.type, refreshTokenByTimeOutWorker);
}
